import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import type {
  CameraTarget,
  CameraViewType,
  Language,
  MapType,
  MeasurementUnitSystem
} from '@strava/mre';

import { MAP_TYPES } from '../constants';
import { getWorldBounds2FromLatLngArray } from '../helpers/getWorldBounds2FromLatLngArray';
import { LatLng, MREStateContextType } from '../types';
import { useMRE } from './useMRE';

/* This context and the associated provider & hook below are used to manage MUTABLE state of the MRE.
 It provides a way to set and get various properties related to the MRE, such as map type, surface identifier, and more.
 
 Defaults are set in the useEffect below the setters.

 Note: Immutable state related to the MRE is managed by the MREContext and useMRE hook.
*/

export const MREStateContext = createContext<MREStateContextType>({
  polyline: null,
  setPolyline: () => {},
  mapboxToken: null,
  setMapboxToken: () => {},
  surfaceIdentifier: null,
  setSurfaceIdentifier: () => {},
  mapType: null,
  setMapType: () => {},
  showPOIs: false,
  setShowPOIs: () => {},
  desiredLanguage: null,
  setDesiredLanguage: () => {},
  unitSystem: null,
  setUnitSystem: () => {},
  cameraViewType: null,
  setCameraViewType: () => {},
  zoomSpeed: 0,
  setZoomSpeed: () => {},
  pageIsUnloadingRef: { current: false }, // Ref to track if the page is unloading (See useEffect in Map.tsx)
  isFlyoverInProgress: false,
  setIsFlyoverInProgress: () => {}, // Note: setIsFlyoverInProgress does not start a flyover/call a function on the MRE, but rather just helps us track the flyover state
  resetCameraAndContent: () => {},
  flyoverEndTargetRef: { current: undefined }, // Ref to track the end target of the current flyover, if any
  showPOIsOnFlyoverCompleteRef: { current: undefined } // Ref to track whether to show POIs when the current flyover completes, if any
});
export interface MREStateProviderProps {
  children: ReactNode;
}

export const MREStateContextProvider: React.FC<MREStateProviderProps> = ({
  children
}) => {
  const { terrainEngine } = useMRE();

  // polyline
  // Used to store the current polyline, if any, for use in resetting the camera after a flyover ends
  const [polyline, setPolyline] = useState<LatLng[] | null>(null);

  // mapboxToken
  const [_mapboxToken, _setMapboxToken] = useState<string | null>(null);

  const setMapboxToken = useCallback(
    (mapboxToken: string) => {
      // Set the Mapbox token for the terrain engine
      terrainEngine &&
        terrainEngine.setServerConfig(
          {
            mapboxServerSecret: mapboxToken
          },
          true // keepExistingFiles
        );
      _setMapboxToken(mapboxToken);
    },
    [_setMapboxToken, terrainEngine]
  );

  // surfaceIdentifier
  const [_surfaceIdentifier, _setSurfaceIdentifier] = useState<string | null>(
    null
  );

  const setSurfaceIdentifier = useCallback(
    (surfaceIdentifier: string) => {
      // Set the surface identifier for the terrain engine
      // This is used to form the groupId tag for Strava tiles
      terrainEngine && terrainEngine.setSurfaceIdentifier(surfaceIdentifier);
      _setSurfaceIdentifier(surfaceIdentifier);
    },
    [_setSurfaceIdentifier, terrainEngine]
  );

  // desiredLanguage
  const [_desiredLanguage, _setDesiredLanguage] = useState<Language | null>(
    null
  );

  const setDesiredLanguage = useCallback(
    (desiredLanguage: Language) => {
      terrainEngine && terrainEngine.setDesiredLanguage(desiredLanguage);
      _setDesiredLanguage(desiredLanguage);
    },
    [_setDesiredLanguage, terrainEngine]
  );

  // _unitSystem
  const [_unitSystem, _setUnitSystem] = useState<MeasurementUnitSystem | null>(
    null
  );

  const setUnitSystem = useCallback(
    (unitSystem: MeasurementUnitSystem) => {
      terrainEngine && terrainEngine.setUnitSystem(unitSystem);
    },
    [terrainEngine]
  );

  // cameraViewType
  const [_cameraViewType, _setCameraViewType] = useState<CameraViewType | null>(
    null
  );

  const setCameraViewType = useCallback(
    (cameraViewType: CameraViewType) => {
      terrainEngine &&
        terrainEngine.getCamera().setCameraViewType(cameraViewType);
      _setCameraViewType(cameraViewType);
    },
    [_setCameraViewType, terrainEngine]
  );

  // showPOIs
  const [_showPOIs, _setShowPOIs] = useState<boolean>(false);

  const setShowPOIs = useCallback(
    (showPOIs: boolean) => {
      terrainEngine &&
        terrainEngine.getContent().getStravaPoiFilter().setShow(showPOIs);
      _setShowPOIs(showPOIs);
    },
    [_setShowPOIs, terrainEngine]
  );

  // mapType
  const [_mapType, _setMapType] = useState<MapType>(
    MAP_TYPES.STRAVA_TOPO_LIGHT
  );

  const setMapType = useCallback(
    (mapType: MapType) => {
      terrainEngine && terrainEngine.setMapType(mapType);
      _setMapType(mapType);
    },
    [_setMapType, terrainEngine]
  );

  // zoomSpeed
  const [_zoomSpeed, _setZoomSpeed] = useState<number>(0);
  const setZoomSpeed = useCallback(
    (zoomSpeed: number) => {
      if (terrainEngine) {
        terrainEngine.getCamera().setZoomSpeed(zoomSpeed);
      }
      _setZoomSpeed(zoomSpeed);
    },
    [_setZoomSpeed, terrainEngine]
  );

  // pageIsUnloading

  const pageIsUnloadingRef = useRef<boolean>(false);

  // flyoverEndTargetRef - Used to store the end target of the current flyover
  const flyoverEndTargetRef = useRef<CameraTarget>();

  // showPOIsOnFlyoverCompleteRef - Used to store the showPOIs state to use when the flyover completes
  // This is set to the current state when the flyover starts
  const showPOIsOnFlyoverCompleteRef = useRef<boolean | undefined>(undefined);

  // isFlyoverInProgress
  // Note: setIsFlyoverInProgress does not start a flyover/call a function on the MRE, but rather just helps us track the flyover state
  const [isFlyoverInProgress, setIsFlyoverInProgress] = useState(false);

  // resetCameraAndContent - Resets the camera and content to a default state after a flyover ends
  // - If a polyline is set, it flies to the bounds of the polyline
  // - If showPOIsOnFlyoverCompleteRef is set, it sets showPOIs to that value
  // This function is used internally after a flyover ends, but is also exposed via context for external use if needed
  const resetCameraAndContent = useCallback(() => {
    if (polyline && terrainEngine) {
      // Restore showPOIs state to how it was prior to the flyover
      showPOIsOnFlyoverCompleteRef?.current &&
        setShowPOIs(showPOIsOnFlyoverCompleteRef.current);

      // Fly to the bounds of the polyline with a margin
      terrainEngine
        .getCamera()
        .flyToBounds(
          getWorldBounds2FromLatLngArray(polyline),
          0,
          0,
          0,
          { top: 0.05, right: 0.1, bottom: 0.05, left: 0.1 },
          undefined
        );
    }
  }, [polyline, setShowPOIs, terrainEngine]);

  // DEFAULTS - set defaults when terrain engine when available
  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    //
    setMapType(MAP_TYPES.STRAVA_TOPO_LIGHT);

    // Sync cameraViewType in state with the terrain engine
    setCameraViewType(terrainEngine.getCamera().getCameraViewType());
  }, [terrainEngine, setMapType, setCameraViewType]);

  const mreStateContextValue: MREStateContextType = {
    polyline,
    setPolyline,
    mapboxToken: _mapboxToken,
    setMapboxToken,
    surfaceIdentifier: _surfaceIdentifier,
    setSurfaceIdentifier,
    mapType: _mapType,
    setMapType,
    showPOIs: _showPOIs,
    setShowPOIs,
    desiredLanguage: _desiredLanguage,
    setDesiredLanguage,
    unitSystem: _unitSystem,
    setUnitSystem,
    cameraViewType: _cameraViewType,
    setCameraViewType,
    zoomSpeed: _zoomSpeed,
    setZoomSpeed,
    pageIsUnloadingRef,
    isFlyoverInProgress,
    setIsFlyoverInProgress,
    resetCameraAndContent,
    flyoverEndTargetRef,
    showPOIsOnFlyoverCompleteRef
  };

  return (
    <MREStateContext.Provider value={mreStateContextValue}>
      {children}
    </MREStateContext.Provider>
  );
};

export const useMREState = (): MREStateContextType => {
  const context = useContext(MREStateContext);
  if (!context) {
    throw new Error(
      'useMREState must be used within the MREStateContext.Provider'
    );
  }
  return context;
};
